import React, { type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { Hide } from '@atlaskit/primitives/compiled/hide';
import { Text } from '@atlaskit/primitives/compiled/text';
import Image from '@atlaskit/image/image';
import FadeIn from '@atlaskit/motion/fade-in';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import homeLight from '../../assets/images/Home.gif';
import homeDark from '../../assets/images/Home-dark.gif';
import { spotlight, editorForYouSpotlight } from '../../messages.ts';
import { useTour } from '../../hooks/useNthUserOrientationStore.ts';
import { useSpotlightAnalytics } from '../../hooks/useSpotlightAnalytics.ts';
import homePlaceholderLight from '../../assets/images/Home-placeholder.png';
import homePlaceholderDark from '../../assets/images/Home-placeholder-dark.png';
import { usePreloadGif } from '../../hooks/usePreloadGif.ts';

import { SpotlightStepCount } from '@atlaskit/spotlight/step-count';
import { SpotlightSecondaryAction } from '@atlaskit/spotlight/secondary-action';
import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';
import { SpotlightMedia } from '@atlaskit/spotlight/media';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightFooter } from '@atlaskit/spotlight/footer';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightControls } from '@atlaskit/spotlight/controls';
import { SpotlightCard } from '@atlaskit/spotlight/card';
import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightActions } from '@atlaskit/spotlight/actions';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

interface EditorForYouSpotlightProps {
	children: ReactNode;
}
const STEP_ID = 'nth-user-orientation-editor-for-you-spotlight';
export const EditorForYouSpotlight: React.FC<EditorForYouSpotlightProps> = ({ children }) => {
	const { reset, next, back, currentStep, progress, hasInteracted, active } = useTour(STEP_ID);
	const { formatMessage } = useIntl();
	const isVisible = currentStep === STEP_ID;
	const { PopoverContent } = useChoreographerAssets();

	const currentSrc = usePreloadGif({
		gif: { light: homeLight, dark: homeDark },
		placeholder: { light: homePlaceholderLight, dark: homePlaceholderDark },
		skip: !active,
	});

	const { handleNext, handleBack, handleDismiss } = useSpotlightAnalytics({
		spotlightId: STEP_ID,
		isVisible,
		onNext: next,
		onBack: back,
		onDismiss: reset,
	});

	return (
		<PopoverProvider>
			<PopoverTarget>{children}</PopoverTarget>
			<Hide below="md">
				<PopoverContent
					shouldDismissOnClickOutside={false}
					next={handleNext}
					back={handleBack}
					dismiss={handleDismiss}
					placement="right-end"
					offset={[0, 24]}
					isVisible={isVisible}
					messageId={STEP_ID}
					messageType={hasInteracted ? 'transactional' : 'engagement'}
				>
					{(() => {
						const spotlightCard = (
							<SpotlightCard testId={STEP_ID}>
								<SpotlightHeader>
									<SpotlightHeadline>
										{formatMessage(editorForYouSpotlight.headline)}
									</SpotlightHeadline>
									<SpotlightControls>
										<SpotlightDismissControl />
									</SpotlightControls>
								</SpotlightHeader>
								<SpotlightMedia>
									<Image src={currentSrc} alt="" />
								</SpotlightMedia>
								<SpotlightBody>
									<Text>{formatMessage(editorForYouSpotlight.body)}</Text>
								</SpotlightBody>
								<SpotlightFooter>
									<SpotlightStepCount>
										{formatMessage(spotlight.progress, progress)}
									</SpotlightStepCount>
									<SpotlightActions>
										<SpotlightSecondaryAction>
											{formatMessage(spotlight.back)}
										</SpotlightSecondaryAction>
										<SpotlightPrimaryAction>{formatMessage(spotlight.next)}</SpotlightPrimaryAction>
									</SpotlightActions>
								</SpotlightFooter>
							</SpotlightCard>
						);
						// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
						return fg('platform-dst-motion-uplift-spotlight') ? (
							spotlightCard
						) : (
							<FadeIn entranceDirection="left">
								{(props) => <div {...props}>{spotlightCard}</div>}
							</FadeIn>
						);
					})()}
				</PopoverContent>
			</Hide>
		</PopoverProvider>
	);
};
